import { defineStore } from 'pinia'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
  }),
  actions: {
    /**
     * Получить настройки модуля по ID
     * @param {number} moduleId - ID модуля
     * @returns {object} - объект настроек модуля
     */
    getModuleSettings(moduleId) {
      if (!this.user?.modules) return {};
      const module = this.user.modules.find(m => m.id === parseInt(moduleId));
      return module?.settings || {};
    },
    
    /**
     * Проверить, скрыт ли элемент по настройке модуля
     * @param {number} moduleId - ID модуля
     * @param {string} settingKey - ключ настройки
     * @returns {boolean} - true если элемент скрыт
     */
    isHiddenBySetting(moduleId, settingKey) {
      const settings = this.getModuleSettings(moduleId);
      return settings[settingKey] === true;
    },
    
    set_user(user) {
      this.user = user
    },
    async attempt_user() {
      return axios.get('/api/v1/me')
        .then((response) => {
          this.user = response.data.data
          return response
        })
        .catch((error) => {
          this.user = null
          throw error
        })
    },
    async logout() {
      return axios.post('/logout')
        .then((response) => {
          this.$reset()
        })
        .catch((error) => {
          console.error(error)
          throw error
        })
    },
    
    // Метод для смены пользователя - выход и переход на страницу выбора пользователя
    async changeUser() {
      try {
        await this.logout()
        window.location.href = '/auth/user-select'
        return Promise.resolve()
      } catch (error) {
        console.error('Ошибка при смене пользователя:', error)
        return Promise.reject(error)
      }
    },
    
    // Метод для авторизации по ПИН-коду
    async loginWithPin(userId, pinCode, moduleRedirectId = null) {
      const requestData = { 
        user_id: userId, 
        pin_code: pinCode,
        module_redirect_id: moduleRedirectId
      };
      
      return axios.post('/api/auth/verify-pin', 
        requestData, 
        { withCredentials: true }
      )
      .then((response) => {
        if (response.data.success) {
          // После успешной авторизации - попытка загрузить данные пользователя
          this.attempt_user()
            .catch(err => console.error('Ошибка загрузки пользователя после авторизации:', err))
          
          return response.data
        } else {
          throw new Error(response.data.message || 'Ошибка авторизации')
        }
      })
    },
    
    // Метод для загрузки списка пользователей
    async getUsers() {
      return axios.post('/api/auth/get-users', {}, { withCredentials: true })
        .then(response => {
          if (response.data.success) {
            return response.data.data
          } else {
            throw new Error('Не удалось загрузить список пользователей')
          }
        })
    }
  },
  getters: {
    currentUser: (state) => state.user,
    // Права роли из /me: {modules: {"<id модуля>": "full|read"}, sections: {"<ключ раздела>": "full|read"}}.
    // Отсутствие ключа = доступа нет. Администратор получает с бэкенда полный доступ ко всему.
    access: (state) => state.user?.access || {modules: {}, sections: {}},
    // Уровень доступа к модулю: 'full', 'read' или null
    moduleLevel: (state) => (moduleId) => {
      if (moduleId === null || moduleId === undefined || moduleId === '') return null
      return state.user?.access?.modules?.[String(moduleId)] ?? null
    },
    // Модуль виден (полный доступ или чтение)
    canModule: (state) => (moduleId) => {
      if (moduleId === null || moduleId === undefined || moduleId === '') return false
      const level = state.user?.access?.modules?.[String(moduleId)] ?? null
      return level === 'full' || level === 'read'
    },
    // В модуле разрешено изменение
    canEditModule: (state) => (moduleId) => {
      if (moduleId === null || moduleId === undefined || moduleId === '') return false
      return (state.user?.access?.modules?.[String(moduleId)] ?? null) === 'full'
    },
    // Уровень доступа к разделу администрирования: 'full', 'read' или null
    sectionLevel: (state) => (key) => {
      if (!key) return null
      return state.user?.access?.sections?.[String(key)] ?? null
    },
    // Раздел виден (полный доступ или чтение)
    canSection: (state) => (key) => {
      if (!key) return false
      const level = state.user?.access?.sections?.[String(key)] ?? null
      return level === 'full' || level === 'read'
    },
    // В разделе разрешено изменение
    canEditSection: (state) => (key) => {
      if (!key) return false
      return (state.user?.access?.sections?.[String(key)] ?? null) === 'full'
    },
    isSuperAdmin: (state) => {
      if (!state.user?.role) return false;
      const roleName = state.user.role.name || state.user.role;
      return roleName === 'Администратор' || roleName === 'Супер администратор';
    },
    isAdmin: (state) => {
      if (!state.user?.role) return false;
      const roleName = state.user.role.name || state.user.role;
      return roleName === 'Администратор' || roleName === 'Супер администратор';
    },
  },
})
